import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable } from 'rxjs';
import { Microsoft365GroupResource } from '../helpdesk/shared/resources/microsoft-365-group.resource';
import { MicrosoftCallQueueSearchResource } from '../helpdesk/shared/resources/microsoft-call-queue-search.resource';
import { MicrosoftAutoAttendantsSearchResource } from '../helpdesk/shared/resources/microsoft-auto-attendants-search.resource';
import { ZiroSelectOption } from '../forms/fields/select/ziro-select.component';
import { AccountType, Microsoft365GroupType, VoiceAppViewEntityType } from '../shared/models/generated/ziroModels';
import { TeamsAutoAttendantsAccountSearchResource } from './resources/teams-auto-attendants-account-search.resource';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class TeamsAutoAttendantsDestinationsSearchService {
  private _ms365GroupResource = inject(Microsoft365GroupResource);
  private _msCallQueueSearchResource = inject(MicrosoftCallQueueSearchResource);
  private _msAutoAttendantsSearchResource = inject(MicrosoftAutoAttendantsSearchResource);
  private _msTeamsAutoAttendantsAccountSearch = inject(TeamsAutoAttendantsAccountSearchResource);
  private _translateService = inject(TranslateService);

  private _groupTypeLabelKeys: Record<Microsoft365GroupType, string> = {
    [Microsoft365GroupType.M365_GROUP]: 'tkey;shared.microsoft_365_group.type.m365_group',
    [Microsoft365GroupType.DISTRIBUTION_LIST]: 'tkey;shared.microsoft_365_group.type.distribution_list',
  };

  searchUsers(tenantId: string, term: string): Observable<ZiroSelectOption[]> {
    const query = term?.trim() ?? '';
    return this._msTeamsAutoAttendantsAccountSearch.search(tenantId, query, AccountType.USER).pipe(
      map((users) =>
        users.map((user) => {
          const hasPhoneNumber = user.hasPhoneNumber;
          return {
            label: user.displayName ?? user.userPrincipalName,
            value: { ...user, displayName: user.displayName ?? user.userPrincipalName },
            secondaryContent: user.userPrincipalName ?? '',
            disabled: !hasPhoneNumber,
            disabledTooltip: !hasPhoneNumber
              ? 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.buttons.user.no_phone_number.tooltip'
              : undefined,
          };
        }),
      ),
    );
  }

  searchResourceAccounts(tenantId: string, term: string): Observable<ZiroSelectOption[]> {
    const query = term?.trim() ?? '';
    return this._msTeamsAutoAttendantsAccountSearch.search(tenantId, query, AccountType.RESOURCE_ACCOUNT).pipe(
      map((accounts) =>
        accounts.map((account) => ({
          label: account.displayName ?? account.userPrincipalName,
          value: { ...account, displayName: account.displayName ?? account.userPrincipalName },
          secondaryContent: account.userPrincipalName ?? '',
        })),
      ),
    );
  }

  searchVoicemailGroups(tenantId: string, term: string): Observable<ZiroSelectOption[]> {
    const query = term?.trim() ?? '';

    return this._ms365GroupResource.search(tenantId, query).pipe(
      map((groups) =>
        groups.map((group) => ({
          label: group.name,
          value: { ...group, displayName: group.name },
          secondaryContent: group.groupType
            ? this._translateService.instant(this._groupTypeLabelKeys[group.groupType])
            : '',
        })),
      ),
    );
  }

  searchVoiceApps(tenantId: string, term: string): Observable<ZiroSelectOption[]> {
    const query = term?.trim() ?? '';

    return forkJoin([
      this._msCallQueueSearchResource.search(tenantId, query),
      this._msAutoAttendantsSearchResource.search(tenantId, query),
    ]).pipe(
      map(([callQueues, autoAttendants]) => [
        ...callQueues.map((cq) => ({
          label: cq.name,
          value: {
            id: cq.callQueueId,
            guid: cq.callQueueId,
            displayName: cq.name,
            type: VoiceAppViewEntityType.CALL_QUEUE,
          },
          group: 'Call Queue',
        })),
        ...autoAttendants.map((aa) => ({
          label: aa.name,
          value: { id: aa.id, guid: aa.id, displayName: aa.name, type: VoiceAppViewEntityType.AUTO_ATTENDANT },
          group: 'Auto Attendant',
        })),
      ]),
    );
  }
}
